// A deliberately small Markdown reader for model output (spec §13: "render paragraphs/bullets
// safely as text, never innerHTML"). It produces a tree the page renders with React elements, so
// nothing the model writes can become markup: an unmatched `<b>` stays the literal text `<b>`.
//
// Why not a Markdown library: the summarisers are told to emit plain paragraphs and bullets with
// at most bold/italic/code, and a full parser would add raw-HTML and link handling we would then
// have to switch off and test. This covers what they produce and degrades to plain text for the
// rest. Pure and DOM-free so it is unit-tested under node.

export type InlineNode =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: InlineNode[] }
  | { kind: 'em'; children: InlineNode[] }
  | { kind: 'code'; text: string };

export type MarkdownBlock =
  | { kind: 'paragraph'; inlines: InlineNode[] }
  | { kind: 'heading'; inlines: InlineNode[] }
  | { kind: 'list'; ordered: boolean; items: InlineNode[][] };

/** Model output is bounded server-side (20k chars); this is a second guard against a runaway render. */
export const MARKDOWN_MAX_CHARS = 50_000;
const MAX_INLINE_DEPTH = 3;

const HEADING_RE = /^\s{0,3}#{1,6}\s+(.*)$/u;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/u;
const ORDERED_RE = /^\s*\d{1,3}[.)]\s+(.*)$/u;
const RULE_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/u;

export function parseMarkdownLite(source: string | null | undefined): MarkdownBlock[] {
  if (!source) return [];
  const text = source.length > MARKDOWN_MAX_CHARS ? `${source.slice(0, MARKDOWN_MAX_CHARS)}…` : source;
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: 'paragraph', inlines: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };
  const flushList = (): void => {
    if (!list) return;
    blocks.push({ kind: 'list', ordered: list.ordered, items: list.items.map((item) => parseInline(item)) });
    list = null;
  };

  for (const rawLine of text.split(/\r?\n/u)) {
    const line = rawLine.replace(/\s+$/u, '');
    if (line.trim() === '' || RULE_RE.test(line)) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = HEADING_RE.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'heading', inlines: parseInline(heading[1] ?? '') });
      continue;
    }
    const bullet = BULLET_RE.exec(line);
    const ordered = bullet ? null : ORDERED_RE.exec(line);
    const item = bullet ?? ordered;
    if (item) {
      const isOrdered = ordered !== null;
      flushParagraph();
      if (list && list.ordered !== isOrdered) flushList();
      if (!list) list = { ordered: isOrdered, items: [] };
      list.items.push(item[1] ?? '');
      continue;
    }
    // Why: a plain line after bullets starts prose again — the offline summariser writes its
    // closing sentence straight after its bullet list with no blank line between.
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Inline emphasis: `**strong**`, `__strong__`, `*em*`, `_em_` (word-bounded) and `` `code` ``. */
export function parseInline(text: string, depth = 0): InlineNode[] {
  const nodes: InlineNode[] = [];
  let buffer = '';
  const pushText = (): void => {
    if (buffer !== '') {
      nodes.push({ kind: 'text', text: buffer });
      buffer = '';
    }
  };

  let i = 0;
  while (i < text.length) {
    const char = text[i] ?? '';
    const next = text[i + 1] ?? '';

    if (char === '\\' && next !== '' && '\\`*_'.includes(next)) {
      buffer += next;
      i += 2;
      continue;
    }

    if (char === '`') {
      const close = text.indexOf('`', i + 1);
      if (close > i + 1) {
        pushText();
        nodes.push({ kind: 'code', text: text.slice(i + 1, close) });
        i = close + 1;
        continue;
      }
    }

    if (depth < MAX_INLINE_DEPTH) {
      const strong = matchSpan(text, i, char === '*' && next === '*' ? '**' : char === '_' && next === '_' ? '__' : null);
      if (strong) {
        pushText();
        nodes.push({ kind: 'strong', children: parseInline(strong.inner, depth + 1) });
        i = strong.end;
        continue;
      }
      const em = matchSpan(text, i, char === '*' || char === '_' ? char : null);
      if (em) {
        pushText();
        nodes.push({ kind: 'em', children: parseInline(em.inner, depth + 1) });
        i = em.end;
        continue;
      }
    }

    buffer += char;
    i += 1;
  }
  pushText();
  return nodes;
}

/**
 * Finds `marker…marker` starting at `from`. The opening marker must be followed by a non-space
 * and the closing one preceded by a non-space; an underscore marker must also sit at a word
 * boundary, so `snake_case_names` stay literal.
 */
function matchSpan(text: string, from: number, marker: string | null): { inner: string; end: number } | null {
  if (!marker) return null;
  const start = from + marker.length;
  const after = text[start] ?? '';
  if (after === '' || /\s/u.test(after) || after === marker[0]) return null;
  if (marker.startsWith('_') && from > 0 && /[\p{L}\p{N}]/u.test(text[from - 1] ?? '')) return null;

  let cursor = start;
  while (cursor < text.length) {
    const close = text.indexOf(marker, cursor);
    if (close === -1) return null;
    const before = text[close - 1] ?? '';
    const following = text[close + marker.length] ?? '';
    const boundaryOk = !marker.startsWith('_') || following === '' || !/[\p{L}\p{N}]/u.test(following);
    if (!/\s/u.test(before) && before !== '\\' && boundaryOk) {
      return { inner: text.slice(start, close), end: close + marker.length };
    }
    cursor = close + marker.length;
  }
  return null;
}

/** The plain text of a block, for accessible names and tests. */
export function inlineText(nodes: readonly InlineNode[]): string {
  return nodes
    .map((node) => {
      switch (node.kind) {
        case 'text':
        case 'code':
          return node.text;
        case 'strong':
        case 'em':
          return inlineText(node.children);
        default:
          return '';
      }
    })
    .join('');
}
